import fs from 'fs';
import path from 'path';
import chalk from 'chalk';

import { MD } from '../util/const';
import {
  getFileUris,
  isValidRegex,
  resetGlobCache,
  resolveSourcePath,
  updateTitle,
  syncWikilinks,
  syncWikilinksBatch,
} from '../util/util';


// result types

interface RenameResult {
  renames: { old: string; new: string }[];
  changed: string[];
  errors: string[];
}

// rename command

export function rename(oldFname: string, newFname: string, opts?: any): void {
  const result: RenameResult | string = opts?.regex
    ? renameRegex(oldFname, newFname, opts)
    : renameSingle(oldFname, newFname, opts);
  if (typeof result === 'string') {
    console.error(chalk.red(result));
  } else {
    formatOutput(result, oldFname);
  }
}

// single rename — find by basename, rename in place

function renameSingle(oldFname: string, newFname: string, opts?: any): RenameResult | string {
  const cwd: string = process.cwd();
  resetGlobCache();
  const sourcePath: string | undefined = resolveSourcePath(oldFname, cwd);
  if (sourcePath === undefined) {
    // zombie refs — file doesn't exist but wikilinks reference it
    const { changed, errors } = syncWikilinks(oldFname, newFname);
    return { renames: [], changed, errors };
  }
  // rename file in place
  const destPath: string = path.join(path.dirname(sourcePath), newFname + MD);
  try {
    fs.renameSync(sourcePath, destPath);
  } catch (e: any) {
    return `rename failed: ${e}`;
  }
  // update title
  const titleUpdated: boolean = updateTitle(destPath, newFname, opts);
  // sync wikilinks
  resetGlobCache();
  const { changed, errors } = syncWikilinks(oldFname, newFname);
  // merge title-updated into changed
  if (titleUpdated && !changed.includes(newFname)) {
    changed.unshift(newFname);
  }
  return {
    renames: [{ old: oldFname, new: newFname }],
    changed,
    errors,
  };
}

// regex rename — bulk pattern matching on filenames

function renameRegex(oldFname: string, newFname: string, opts?: any): RenameResult | string {
  if (!isValidRegex(oldFname)) {
    return '"oldFname" is not a valid regex';
  }
  const oldSrchPat: RegExp = new RegExp(oldFname, 'g');
  const renames: { old: string; new: string }[] = [];
  const renamePairs: [string, string][] = [];
  const titleUpdatedFiles: Set<string> = new Set();
  const errors: string[] = [];
  // phase 1: rename files + update titles
  const vaultFilePaths: string[] = getFileUris();
  for (const fp of vaultFilePaths) {
    const basename: string = path.basename(fp, MD);
    if (basename.match(oldSrchPat)) {
      const newBasename: string = basename.replace(oldSrchPat, newFname);
      const destPath: string = path.join(path.dirname(fp), newBasename + MD);
      try {
        fs.renameSync(fp, destPath);
        renames.push({ old: basename, new: newBasename });
        renamePairs.push([basename, newBasename]);
        if (updateTitle(destPath, newBasename, opts)) {
          titleUpdatedFiles.add(newBasename);
        }
      } catch (e: any) {
        errors.push(`[FILENAME] ${e}`);
      }
    }
  }
  // phase 2: batch sync wikilinks
  const allChanged: Set<string> = new Set(titleUpdatedFiles);
  if (renamePairs.length > 0) {
    resetGlobCache();
    const sync = syncWikilinksBatch(renamePairs);
    for (const b of sync.changed) { allChanged.add(b); }
    for (const e of sync.errors) { errors.push(e); }
  }
  return {
    renames,
    changed: [...allChanged].sort(),
    errors,
  };
}

// output formatting

function formatOutput(result: RenameResult, oldFname: string): void {
  const fnames: string[] = result.renames.map(r => '  ' + r.old + ' -> ' + r.new);
  const files: string[] = result.changed.map(b => '  ' + b);
  if (fnames.length === 0) {
    fnames.push(chalk.dim('  no file named: \'' + oldFname + '\''));
  }
  if (files.length === 0) {
    files.push(chalk.dim('  no wikirefs named: \'' + oldFname + '\''));
  }
  const output: string[] = [
    chalk.green('UPDATED FILENAMES:'),
    ...fnames,
    chalk.green('UPDATED FILE CONTENT:'),
    ...files,
  ];
  if (result.errors.length > 0) {
    output.push(chalk.red('UPDATE FAILED: '));
    for (const e of result.errors) {
      output.push(chalk.red('  ' + e));
    }
  }
  console.log(output.join('\n'));
}
